/**
 * AgentFlowHistory Component
 *
 * Displays the execution history of an AgentFlow workflow by querying
 * the workflow's step traces. Shows a timeline of blocks executed with
 * their inputs, outputs, and status.
 */

import { format } from 'date-fns';
import { AlertCircle, Check, Clock, Hourglass, Loader2, Play, SkipForward, X } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  type StartLOAChaserRequest,
  useCancelLOAChaserMutation,
  useJumpToStepMutation,
  useLazyGetLOAChaserHistoryQuery,
  useLazyGetLOAChaserRunStatusQuery,
  useLazyGetLOAChaserStatusQuery,
  useStartLOAChaserMutation,
  type WorkflowExecutionSummary,
} from '@/services/api/agentflowService';

// ============================================================================
// Flow Step Definitions
// ============================================================================

const LOA_CHASER_FLOWS = {
  non_disclosure_3p: [
    { id: 'day_2_voice_ai', label: 'Day 2: Voice AI Call', index: 0 },
    { id: 'day_3_dialpad_call', label: 'Day 3: RingCentral Call (LOA + limits)', index: 1 },
    { id: 'day_5_email', label: 'Day 5: Email follow-up', index: 2 },
    { id: 'day_7_email', label: 'Day 7: Email follow-up', index: 3 },
    { id: 'day_9_email', label: 'Day 9: Email follow-up', index: 4 },
  ],
  progressive_1p: [
    { id: 'day_2_email', label: 'Day 2: Email follow-up', index: 0 },
    { id: 'day_5_email', label: 'Day 5: Email follow-up', index: 1 },
    { id: 'day_6_dialpad_call', label: 'Day 6: RingCentral Call (LOA)', index: 2 },
    { id: 'day_8_email', label: 'Day 8: Email follow-up', index: 3 },
  ],
  disclosure: [
    { id: 'day_2_voice_ai', label: 'Day 2: Voice AI Call', index: 0 },
    { id: 'day_3_dialpad_call', label: 'Day 3: RingCentral Call (LOA + Dec page)', index: 1 },
    { id: 'day_3_email', label: 'Day 3: Email follow-up', index: 2 },
    { id: 'day_3_text_claimant', label: 'Day 3: Text claimant for Dec page', index: 3 },
    { id: 'day_6_disclosure_email', label: 'Day 6: Email follow-up', index: 4 },
    { id: 'day_8_disclosure_email', label: 'Day 8: Email follow-up', index: 5 },
    { id: 'day_10_disclosure_email', label: 'Day 10: Email follow-up', index: 6 },
  ],
} as const;

type FlowId = keyof typeof LOA_CHASER_FLOWS;

// ============================================================================
// Types
// ============================================================================

interface StepTrace {
  step_id: string;
  step_type: string;
  input: Record<string, unknown>;
  output?: Record<string, unknown> | null;
  status: 'pending' | 'in_progress' | 'running' | 'completed' | 'failed' | 'waiting';
  started_at?: string | null;
  completed_at?: string | null;
  error?: string | null;
  // Wait progress tracking
  wait_until?: string | null;
  wait_label?: string | null;
}

interface AgentFlowHistoryProps {
  /** Matter ID for API calls */
  matterId: string;
  /** Coverage ID for API calls */
  coverageId: string;
  /** Task ID for workflow lookup */
  taskId: string;
  /** Whether the history modal is open */
  open: boolean;
  /** Callback when modal is closed */
  onOpenChange: (open: boolean) => void;
  /** Auto-refresh interval in ms (default: 5000ms) */
  refreshInterval?: number;
  /** Flow ID to determine available steps for jump (default: non_disclosure_3p) */
  flowId?: FlowId;
}

// ============================================================================
// Helper Components
// ============================================================================

const StepStatusIcon: React.FC<{ status: StepTrace['status'] }> = ({
  status,
}) => {
  switch (status) {
    case 'completed':
      return <Check className="h-4 w-4 text-emerald-600" />;
    case 'failed':
      return <X className="h-4 w-4 text-red-600" />;
    case 'in_progress':
    case 'running':
      return <Loader2 className="h-4 w-4 text-violet-600 animate-spin" />;
    case 'waiting':
      return <Hourglass className="h-4 w-4 text-amber-500 animate-pulse" />;
    case 'pending':
      return <Clock className="h-4 w-4 text-zinc-400" />;
    default:
      return <AlertCircle className="h-4 w-4 text-zinc-400" />;
  }
};

const StepStatusBadge: React.FC<{ status: StepTrace['status'] }> = ({
  status,
}) => {
  const variants: Record<string, string> = {
    completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    failed: 'bg-red-100 text-red-800 border-red-200',
    in_progress: 'bg-violet-100 text-violet-800 border-violet-200',
    running: 'bg-violet-100 text-violet-800 border-violet-200',
    waiting: 'bg-amber-50 text-amber-700 border-amber-200',
    pending: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  };

  return (
    <Badge
      variant="secondary"
      className={cn('text-[10px] px-1.5 py-0', variants[status] || variants.pending)}
    >
      {status.replace('_', ' ')}
    </Badge>
  );
};

// ============================================================================
// WaitProgressBar Component - Minimalist, elegant progress indicator
// ============================================================================

const WaitProgressBar: React.FC<{
  startedAt: string;
  waitUntil: string;
  label?: string | null;
}> = ({ startedAt, waitUntil, label }) => {
  const [progress, setProgress] = React.useState(0);
  const [timeRemaining, setTimeRemaining] = React.useState('');

  React.useEffect(() => {
    const calculateProgress = () => {
      const start = new Date(startedAt).getTime();
      const end = new Date(waitUntil).getTime();
      const now = Date.now();

      const totalDuration = end - start;
      const elapsed = now - start;
      const progressPercent = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));

      setProgress(progressPercent);

      // Calculate time remaining
      const remaining = Math.max(0, end - now);
      if (remaining <= 0) {
        setTimeRemaining('Completing...');
      } else {
        const seconds = Math.floor(remaining / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) {
          setTimeRemaining(`${days}d ${hours % 24}h remaining`);
        } else if (hours > 0) {
          setTimeRemaining(`${hours}h ${minutes % 60}m remaining`);
        } else if (minutes > 0) {
          setTimeRemaining(`${minutes}m remaining`);
        } else {
          setTimeRemaining(`${seconds}s remaining`);
        }
      }
    };

    calculateProgress();
    const interval = setInterval(calculateProgress, 1000);
    return () => clearInterval(interval);
  }, [startedAt, waitUntil]);

  return (
    <div className="space-y-2">
      {label && (
        <div className="text-xs text-zinc-500 font-medium">{label}</div>
      )}

      {/* Progress bar container */}
      <div className="relative h-2 bg-zinc-100 rounded-full overflow-hidden">
        {/* Animated gradient background */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-amber-200 via-amber-400 to-amber-500 rounded-full transition-all duration-500 ease-out"
          style={{
            width: `${progress}%`,
            boxShadow: progress > 0 ? '0 0 8px rgba(251, 191, 36, 0.4)' : 'none'
          }}
        />

        {/* Shimmer effect */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"
          style={{
            width: `${progress}%`,
            animation: 'shimmer 2s infinite',
          }}
        />
      </div>

      {/* Progress info */}
      <div className="flex justify-between items-center text-[10px]">
        <span className="text-zinc-400">{Math.round(progress)}% complete</span>
        <span className="text-amber-600 font-medium">{timeRemaining}</span>
      </div>

      {/* Custom shimmer animation style */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
};

// ============================================================================
// PrettyJSON Component - Formatted JSON display with syntax highlighting
// ============================================================================

const PrettyJSON: React.FC<{ data: Record<string, unknown>; label: string }> = ({
  data,
  label,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Format JSON with proper indentation
  const formattedJson = JSON.stringify(data, null, 2);
  const lineCount = formattedJson.split('\n').length;
  const isLarge = lineCount > 10;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-medium text-zinc-700">{label}:</div>
        {isLarge && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-[10px] text-violet-600 hover:text-violet-700"
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </button>
        )}
      </div>
      <pre
        className={cn(
          'text-[11px] bg-zinc-50 border border-zinc-200 rounded p-2 overflow-x-auto font-mono',
          !isExpanded && isLarge && 'max-h-40 overflow-y-auto'
        )}
      >
        {formattedJson}
      </pre>
    </div>
  );
};

// ============================================================================
// WorkflowInputCard Component - Display workflow input parameters
// ============================================================================

const WorkflowInputCard: React.FC<{
  input: Record<string, unknown>;
  startTime?: string | null;
  closeTime?: string | null;
  status?: string;
}> = ({ input, startTime, closeTime, status }) => {
  const [showDetails, setShowDetails] = React.useState(false);

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    try {
      return format(new Date(timestamp), 'MMM d, yyyy h:mm:ss a');
    } catch {
      return timestamp;
    }
  };

  // Extract key fields to display prominently
  const keyFields = ['task_id', 'matter_id', 'coverage_id', 'flow_id', 'disclosure_state'];
  const displayFields: Record<string, unknown> = {};
  const otherFields: Record<string, unknown> = {};

  Object.entries(input).forEach(([key, value]) => {
    if (keyFields.includes(key) && value != null) {
      displayFields[key] = value;
    } else if (value != null) {
      otherFields[key] = value;
    }
  });

  return (
    <div className="border border-violet-200 rounded-lg bg-gradient-to-br from-violet-50/80 to-white mb-4">
      <div
        role="button"
        tabIndex={0}
        className="flex items-center gap-2 p-3 cursor-pointer bg-gradient-to-r from-violet-50 to-transparent hover:from-violet-100 transition-colors"
        onClick={() => setShowDetails(!showDetails)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowDetails(!showDetails);
          }
        }}
      >
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-violet-200 text-violet-800 text-xs font-semibold shrink-0">
          ⚙️
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-zinc-900">
            Workflow Configuration
          </div>
          <div className="text-xs text-zinc-500">
            Started: {formatTimestamp(startTime)}
            {closeTime && ` • Ended: ${formatTimestamp(closeTime)}`}
          </div>
        </div>
        <Badge
          variant="secondary"
          className={cn(
            'text-[10px] px-1.5 py-0',
            status === 'completed' && 'bg-emerald-100 text-emerald-800 border-emerald-200',
            status === 'running' && 'bg-violet-100 text-violet-800 border-violet-200',
            status === 'terminated' && 'bg-zinc-100 text-zinc-600 border-zinc-200',
            status === 'failed' && 'bg-red-100 text-red-800 border-red-200',
          )}
        >
          {status || 'unknown'}
        </Badge>
      </div>

      {showDetails && (
        <div className="p-3 space-y-3 border-t border-violet-200">
          {/* Key Fields */}
          {Object.keys(displayFields).length > 0 && (
            <div className="grid grid-cols-2 gap-2 text-xs">
              {Object.entries(displayFields).map(([key, value]) => (
                <div key={key}>
                  <span className="font-medium text-zinc-700">{key.replace(/_/g, ' ')}:</span>
                  <div className="text-zinc-600 truncate">
                    {typeof value === 'string' ? value : JSON.stringify(value)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Other Fields */}
          {Object.keys(otherFields).length > 0 && (
            <PrettyJSON data={otherFields} label="Additional Parameters" />
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// StepTraceCard Component
// ============================================================================

const StepTraceCard: React.FC<{ trace: StepTrace; index: number }> = ({
  trace,
  index,
}) => {
  const [showDetails, setShowDetails] = React.useState(false);

  // Auto-expand waiting steps to show progress
  const isWaiting = trace.status === 'waiting';
  const effectiveShowDetails = showDetails || isWaiting;

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    try {
      return format(new Date(timestamp), 'MMM d, h:mm:ss a');
    } catch {
      return timestamp;
    }
  };

  const duration =
    trace.started_at && trace.completed_at
      ? Math.round(
          (new Date(trace.completed_at).getTime() -
            new Date(trace.started_at).getTime()) /
            1000,
        )
      : null;

  return (
    <div className={cn(
      "border rounded-lg overflow-hidden transition-all",
      isWaiting
        ? "border-amber-200 bg-gradient-to-br from-amber-50/80 to-white shadow-sm ring-1 ring-amber-100/50"
        : "border-zinc-200 bg-white"
    )}>
      {/* Header */}
      <div
        role="button"
        tabIndex={0}
        className={cn(
          "flex items-center gap-2 p-3 cursor-pointer transition-colors",
          isWaiting
            ? "bg-gradient-to-r from-amber-50 to-transparent hover:from-amber-100"
            : "bg-zinc-50 hover:bg-zinc-100"
        )}
        onClick={() => setShowDetails(!showDetails)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowDetails(!showDetails);
          }
        }}
      >
        <div className={cn(
          "flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold shrink-0",
          isWaiting
            ? "bg-amber-200 text-amber-800"
            : "bg-zinc-200 text-zinc-700"
        )}>
          {index + 1}
        </div>
        <StepStatusIcon status={trace.status} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-zinc-900 truncate">
              {isWaiting && trace.wait_label ? trace.wait_label : trace.step_type}
            </span>
            <StepStatusBadge status={trace.status} />
          </div>
          <div className="text-xs text-zinc-500">
            {isWaiting ? 'Scheduled delay' : `Step ID: ${trace.step_id}`}
          </div>
        </div>
        {duration !== null && (
          <span className="text-xs text-zinc-500 shrink-0">{duration}s</span>
        )}
      </div>

      {/* Details */}
      {effectiveShowDetails && (
        <div className="p-3 space-y-3 border-t border-zinc-200">
          {/* Wait Progress Bar - Show prominently for waiting steps */}
          {isWaiting && trace.started_at && trace.wait_until && (
            <div className="pb-2">
              <WaitProgressBar
                startedAt={trace.started_at}
                waitUntil={trace.wait_until}
                label={trace.wait_label}
              />
            </div>
          )}

          {/* Timestamps */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="font-medium text-zinc-700">Started:</span>
              <div className="text-zinc-600">
                {formatTimestamp(trace.started_at)}
              </div>
            </div>
            {trace.completed_at && (
              <div>
                <span className="font-medium text-zinc-700">Completed:</span>
                <div className="text-zinc-600">
                  {formatTimestamp(trace.completed_at)}
                </div>
              </div>
            )}
            {isWaiting && trace.wait_until && (
              <div>
                <span className="font-medium text-zinc-700">Resumes at:</span>
                <div className="text-zinc-600">
                  {formatTimestamp(trace.wait_until)}
                </div>
              </div>
            )}
          </div>

          {/* Error */}
          {trace.error && (
            <div className="rounded border border-red-200 bg-red-50 p-2">
              <div className="text-xs font-medium text-red-800 mb-1">
                Error:
              </div>
              <div className="text-xs text-red-700 font-mono">
                {trace.error}
              </div>
            </div>
          )}

          {/* Input - Don't show for waiting steps as it's redundant */}
          {!isWaiting && Object.keys(trace.input || {}).length > 0 && (
            <PrettyJSON data={trace.input} label="Input" />
          )}

          {/* Output */}
          {trace.output && Object.keys(trace.output).length > 0 && (
            <PrettyJSON data={trace.output} label="Output" />
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ExecutionListItem Component
// ============================================================================

interface ExecutionListItemProps {
  execution: WorkflowExecutionSummary;
  onClick: () => void;
  isSelected?: boolean;
}

const ExecutionListItem: React.FC<ExecutionListItemProps> = ({
  execution,
  onClick,
  isSelected,
}) => {
  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'MMM d, h:mm a');
    } catch {
      return dateStr;
    }
  };

  const statusColors: Record<string, string> = {
    running: 'bg-violet-100 text-violet-800 border-violet-200',
    completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    terminated: 'bg-zinc-100 text-zinc-600 border-zinc-200',
    failed: 'bg-red-100 text-red-800 border-red-200',
    canceled: 'bg-amber-100 text-amber-800 border-amber-200',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 p-3 text-left rounded-lg border transition-colors hover:bg-zinc-50',
        isSelected ? 'border-violet-300 bg-violet-50' : 'border-zinc-200'
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Badge
            variant="secondary"
            className={cn(
              'text-[10px] px-1.5 py-0',
              statusColors[execution.status] || statusColors.terminated
            )}
          >
            {execution.status}
          </Badge>
          <span className="text-xs text-zinc-500">
            {formatDate(execution.start_time)}
          </span>
        </div>
        <div className="text-[10px] text-zinc-400 mt-0.5 truncate">
          Run: {execution.run_id.slice(0, 8)}...
        </div>
      </div>
      {execution.status === 'running' ? (
        <Loader2 className="h-4 w-4 text-violet-600 animate-spin shrink-0" />
      ) : execution.status === 'completed' ? (
        <Check className="h-4 w-4 text-emerald-600 shrink-0" />
      ) : execution.status === 'failed' ? (
        <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
      ) : (
        <Clock className="h-4 w-4 text-zinc-400 shrink-0" />
      )}
    </button>
  );
};

// ============================================================================
// AgentFlowHistory Component
// ============================================================================

export const AgentFlowHistory: React.FC<AgentFlowHistoryProps> = ({
  matterId,
  coverageId,
  taskId,
  open,
  onOpenChange,
  refreshInterval = 5000,
  flowId = 'non_disclosure_3p',
}) => {
  const [triggerStatus, { data: statusData, isLoading: isLoadingStatus, isFetching: isFetchingStatus, error: statusError }] =
    useLazyGetLOAChaserStatusQuery();
  const [triggerHistory, { data: historyData, isLoading: isLoadingHistory }] =
    useLazyGetLOAChaserHistoryQuery();
  const [triggerRunStatus, { data: runStatusData, isLoading: isLoadingRunStatus, isFetching: isFetchingRunStatus }] =
    useLazyGetLOAChaserRunStatusQuery();
  const [jumpToStep] = useJumpToStepMutation();
  const [cancelWorkflow, { isLoading: isCancelling }] = useCancelLOAChaserMutation();
  const [startLOAChaser, { isLoading: isRestarting }] = useStartLOAChaserMutation();
  const [selectedStepIndex, setSelectedStepIndex] = React.useState<string>('');
  const [restartStepIndex, setRestartStepIndex] = React.useState<string>('0');
  const [isJumping, setIsJumping] = React.useState(false);
  const [selectedRunId, setSelectedRunId] = React.useState<string | null>(null);
  const [viewMode, setViewMode] = React.useState<'current' | 'history'>('current');

  // Get available steps - prefer execution_plan from API (has actual step_index)
  // Fall back to hardcoded list if execution_plan is not available
  const executionPlanSteps = (statusData?.execution_plan as Array<{
    step_index: number;
    step_id: string;
    step_type: string;
    label?: string | null;
    is_current?: boolean;
  }>) || [];

  // Build available steps list from execution_plan or fall back to hardcoded
  const availableSteps = executionPlanSteps.length > 0
    ? executionPlanSteps.map((step) => ({
        id: step.step_id,
        label: step.label || step.step_id.replace(/_/g, ' '),
        index: step.step_index,
      }))
    : (LOA_CHASER_FLOWS[flowId] || LOA_CHASER_FLOWS.non_disclosure_3p);

  const handleJumpToStep = async () => {
    if (!selectedStepIndex) {
      toast.error('Please select a step to jump to');
      return;
    }

    setIsJumping(true);
    try {
      await jumpToStep({
        matterId,
        coverageId,
        request: {
          task_id: taskId,
          step_index: parseInt(selectedStepIndex, 10),
        },
      }).unwrap();
      toast.success('Jump signal sent successfully');
      setSelectedStepIndex('');
      // Refresh the workflow status
      triggerStatus({ matterId, coverageId, taskId });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to jump to step';
      toast.error(message);
    } finally {
      setIsJumping(false);
    }
  };

  // Handle cancelling a workflow (for error state or running workflows)
  const handleCancel = async () => {
    try {
      await cancelWorkflow({
        matterId,
        coverageId,
        request: { task_id: taskId, reason: 'Cancelled by user' },
      }).unwrap();
      toast.success('Workflow cancelled');
      // Refresh status
      triggerStatus({ matterId, coverageId, taskId });
      triggerHistory({ matterId, coverageId, taskId });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel workflow';
      toast.error(message);
    }
  };

  // Handle restarting a cancelled/terminated workflow with same inputs
  const handleRestart = async () => {
    // Get workflow input from either current status or selected run
    const workflowInput = activeData?.workflow_input as Record<string, unknown> | undefined;
    if (!workflowInput) {
      toast.error('Cannot restart: workflow input not available');
      return;
    }

    const stepIndex = parseInt(restartStepIndex || '0', 10);

    // Build restart request from the original workflow input
    const request: StartLOAChaserRequest = {
      task_id: taskId,
      flow_id: (workflowInput.flow_id as string) || undefined,
      email_thread_id: (workflowInput.email_thread_id as string) || undefined,
      recipient_email: (workflowInput.recipient_email as string) || undefined,
      phone_number: (workflowInput.phone_number as string) || undefined,
      adjuster_phone: (workflowInput.adjuster_phone as string) || undefined,
      starting_step_index: stepIndex,
      skip_initial_delay: true, // Skip delay when restarting
      email_step_content: (workflowInput.email_step_content as Record<string, { subject: string; body: string }>) || undefined,
    };

    try {
      await startLOAChaser({
        matterId,
        coverageId,
        request,
      }).unwrap();
      toast.success(`Workflow restarted from step ${stepIndex + 1}`);
      // Refresh status and history
      triggerStatus({ matterId, coverageId, taskId });
      triggerHistory({ matterId, coverageId, taskId });
      // Go back to current view
      setSelectedRunId(null);
      setViewMode('current');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to restart workflow';
      toast.error(message);
    }
  };

  // Handle selecting a historical run
  const handleSelectRun = (runId: string) => {
    setSelectedRunId(runId);
    triggerRunStatus({ matterId, coverageId, taskId, runId });
  };

  // Handle going back to history list
  const handleBackToHistory = () => {
    setSelectedRunId(null);
    setViewMode('history');
  };

  // Fetch workflow status and history when modal opens
  React.useEffect(() => {
    if (open) {
      triggerStatus({ matterId, coverageId, taskId });
      triggerHistory({ matterId, coverageId, taskId });
      setSelectedRunId(null);
      setViewMode('current');
    }
  }, [open, matterId, coverageId, taskId, triggerStatus, triggerHistory]);

  // Determine which data to show
  const currentWorkflowStatus = statusData?.workflow_status as string | undefined;
  const isWorkflowRunning = currentWorkflowStatus === 'running';
  const isWorkflowError = currentWorkflowStatus === 'error';
  const workflowErrorMessage = (statusData as { error?: string } | undefined)?.error;
  const hasHistoricalRuns = (historyData?.executions?.length ?? 0) > 0;
  const showHistoryList = viewMode === 'history' || (!isWorkflowRunning && !isWorkflowError && hasHistoricalRuns && !selectedRunId);

  // Get the active data source (current status or selected run)
  const activeData = selectedRunId ? runStatusData : statusData;
  const activeStatus = activeData?.workflow_status as string | undefined;
  const traces = (activeData?.traces || []) as unknown as StepTrace[];
  const isLoading = isLoadingStatus || isLoadingHistory || isLoadingRunStatus;
  const isFetching = isFetchingStatus || isFetchingRunStatus;
  const error = statusError;

  // Check if the active view (current or selected run) can be restarted
  const canRestart = activeStatus === 'terminated' || activeStatus === 'canceled' || activeStatus === 'failed';
  const hasWorkflowInput = Boolean(activeData?.workflow_input);

  // Get the last step index from terminated workflows
  const lastStepIndex = (activeData as { last_step_index?: number | null } | undefined)?.last_step_index;

  // Get restart step options from workflow input's flow_id or fallback to default
  const workflowFlowId = (activeData?.workflow_input?.flow_id as FlowId | undefined) || flowId;
  const restartStepOptions = LOA_CHASER_FLOWS[workflowFlowId] || LOA_CHASER_FLOWS.non_disclosure_3p;

  // Get the label for the last step (for display)
  const lastStepLabel = lastStepIndex !== null && lastStepIndex !== undefined
    ? restartStepOptions.find((step) => step.index === lastStepIndex)?.label || `Step ${lastStepIndex + 1}`
    : null;

  // Set default restart step to the last step when viewing a terminated workflow
  React.useEffect(() => {
    if (canRestart && lastStepIndex !== null && lastStepIndex !== undefined) {
      setRestartStepIndex(String(lastStepIndex));
    }
  }, [canRestart, lastStepIndex]);

  // Auto-refresh while modal is open AND workflow is running (and not viewing historical)
  React.useEffect(() => {
    if (!open || !isWorkflowRunning || selectedRunId) return undefined;

    const interval = setInterval(() => {
      triggerStatus({ matterId, coverageId, taskId });
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [open, matterId, coverageId, taskId, triggerStatus, refreshInterval, isWorkflowRunning, selectedRunId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {selectedRunId ? (
              <>
                <button
                  type="button"
                  onClick={handleBackToHistory}
                  className="text-zinc-500 hover:text-zinc-700 transition-colors"
                >
                  ← Back
                </button>
                <span className="text-zinc-300">|</span>
              </>
            ) : null}
            <span>AgentFlow Execution History</span>
            {isFetching && (
              <Loader2 className="h-4 w-4 text-violet-600 animate-spin" />
            )}
            {activeStatus && activeStatus !== 'running' && (
              <Badge
                variant="secondary"
                className={cn(
                  'text-[10px] px-1.5 py-0',
                  activeStatus === 'completed' && 'bg-emerald-100 text-emerald-800',
                  activeStatus === 'terminated' && 'bg-zinc-100 text-zinc-600',
                  (activeStatus === 'failed' || activeStatus === 'error') && 'bg-red-100 text-red-800',
                  activeStatus === 'not_found' && 'bg-zinc-100 text-zinc-500',
                )}
              >
                {activeStatus === 'error' ? 'Error' : activeStatus.replace('_', ' ')}
              </Badge>
            )}
          </DialogTitle>
          <div className="text-xs text-zinc-500 font-normal">
            {selectedRunId
              ? `Run: ${selectedRunId.slice(0, 12)}...`
              : `Workflow ID: loa-chaser-task-${taskId}`}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <Loader2 className="h-6 w-6 text-violet-600 animate-spin" />
              <span className="text-sm text-zinc-500">
                Loading workflow history...
              </span>
            </div>
          )}

          {/* Error State - API error */}
          {error && (
            <div className="rounded border border-red-200 bg-red-50 p-4">
              <div className="text-sm font-medium text-red-800 mb-1">
                Failed to load workflow history
              </div>
              <div className="text-xs text-red-700">
                {error && typeof error === 'object' && 'data' in error
                  ? JSON.stringify(error.data)
                  : 'An error occurred'}
              </div>
            </div>
          )}

          {/* Workflow Error State - Non-determinism or other workflow errors */}
          {!isLoading && !error && isWorkflowError && (
            <div className="rounded border border-red-200 bg-red-50 p-4">
              <div className="flex items-start gap-3">
                <div className="shrink-0 mt-0.5">
                  <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-red-800 mb-1">
                    Workflow Error
                  </div>
                  <div className="text-xs text-red-700 mb-3">
                    This workflow encountered an internal error and cannot continue.
                    This typically happens when the workflow code was updated while this workflow was running.
                    Please cancel and restart the LOA Chase.
                  </div>
                  {workflowErrorMessage && (
                    <details className="text-xs">
                      <summary className="text-red-600 cursor-pointer hover:text-red-700">
                        Technical details
                      </summary>
                      <pre className="mt-2 p-2 bg-red-100 rounded text-red-800 overflow-x-auto whitespace-pre-wrap text-[10px]">
                        {workflowErrorMessage}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* History List View - Show when no running workflow or explicitly viewing history */}
          {!isLoading && !error && showHistoryList && !selectedRunId && (
            <div className="space-y-2">
              {hasHistoricalRuns ? (
                <>
                  <div className="text-xs text-zinc-500 mb-3">
                    {historyData?.executions?.length || 0} workflow run{(historyData?.executions?.length || 0) !== 1 ? 's' : ''} found
                  </div>
                  <div className="space-y-2">
                    {historyData?.executions?.map((execution) => (
                      <ExecutionListItem
                        key={execution.run_id}
                        execution={execution}
                        onClick={() => handleSelectRun(execution.run_id)}
                        isSelected={selectedRunId === execution.run_id}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 gap-2">
                  <Clock className="h-8 w-8 text-zinc-300" />
                  <span className="text-sm text-zinc-500">
                    No workflow runs found
                  </span>
                  <span className="text-xs text-zinc-400">
                    Start an LOA Chaser workflow to see history
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Detail View - Show traces for current or selected run */}
          {!isLoading && !error && !showHistoryList && (
            <>
              {/* Empty State / Workflow Status Messages */}
              {traces.length === 0 && (
                <div className="space-y-4">
                  {/* Show workflow input even if no traces */}
                  {activeData?.workflow_input && (
                    <WorkflowInputCard
                      input={activeData.workflow_input as Record<string, unknown>}
                      startTime={activeData.start_time}
                      closeTime={activeData.close_time}
                      status={activeStatus}
                    />
                  )}
                  <div className="flex flex-col items-center justify-center py-8 gap-2">
                  {activeStatus === 'terminated' || activeStatus === 'canceled' ? (
                    <>
                      <X className="h-8 w-8 text-zinc-400" />
                      <span className="text-sm text-zinc-600 font-medium">
                        Workflow {activeStatus === 'canceled' ? 'Cancelled' : 'Terminated'}
                      </span>
                      <span className="text-xs text-zinc-400">
                        This workflow was stopped before completion
                      </span>
                      {lastStepLabel && (
                        <span className="text-xs text-amber-600 mt-1">
                          Last active step: {lastStepLabel}
                        </span>
                      )}
                    </>
                  ) : activeStatus === 'completed' ? (
                    <>
                      <Check className="h-8 w-8 text-emerald-500" />
                      <span className="text-sm text-zinc-600 font-medium">
                        Workflow Completed
                      </span>
                      <span className="text-xs text-zinc-400">
                        This workflow finished successfully
                      </span>
                    </>
                  ) : activeStatus === 'failed' ? (
                    <>
                      <AlertCircle className="h-8 w-8 text-red-400" />
                      <span className="text-sm text-zinc-600 font-medium">
                        Workflow Failed
                      </span>
                      <span className="text-xs text-zinc-400">
                        This workflow encountered an error
                      </span>
                      {lastStepLabel && (
                        <span className="text-xs text-amber-600 mt-1">
                          Failed at step: {lastStepLabel}
                        </span>
                      )}
                    </>
                  ) : activeStatus === 'not_found' ? (
                    <>
                      <Clock className="h-8 w-8 text-zinc-300" />
                      <span className="text-sm text-zinc-500">
                        No workflow found
                      </span>
                      <span className="text-xs text-zinc-400">
                        Start an LOA Chaser workflow to see history
                      </span>
                    </>
                  ) : (
                    <>
                      <Clock className="h-8 w-8 text-zinc-300" />
                      <span className="text-sm text-zinc-500">
                        No workflow history available
                      </span>
                      <span className="text-xs text-zinc-400">
                        The workflow may not have started yet
                      </span>
                    </>
                  )}
                  {/* Show view history button if there are historical runs */}
                  {hasHistoricalRuns && (
                    <button
                      type="button"
                      onClick={() => setViewMode('history')}
                      className="mt-4 px-3 py-2 text-xs font-semibold rounded-md border border-zinc-200 text-zinc-600 hover:bg-zinc-50 transition-colors"
                    >
                      View Previous Runs ({historyData?.executions?.length || 0})
                    </button>
                  )}
                  </div>
                </div>
              )}

              {/* Traces List */}
              {traces.length > 0 && (
                <>
                  {/* Workflow Input Card - Show configuration at the top */}
                  {activeData?.workflow_input && (
                    <WorkflowInputCard
                      input={activeData.workflow_input as Record<string, unknown>}
                      startTime={activeData.start_time}
                      closeTime={activeData.close_time}
                      status={activeStatus}
                    />
                  )}
                  <div className="text-xs text-zinc-500 mb-2">
                    {traces.length} step{traces.length !== 1 ? 's' : ''} executed
                  </div>
                  {traces.map((trace, index) => (
                    <StepTraceCard
                      key={trace.step_id}
                      trace={trace}
                      index={index}
                    />
                  ))}
                  {/* Show view history button below traces if there are multiple runs */}
                  {hasHistoricalRuns && (historyData?.executions?.length || 0) > 1 && (
                    <div className="pt-4 flex justify-center">
                      <button
                        type="button"
                        onClick={() => setViewMode('history')}
                        className="px-3 py-2 text-xs font-medium text-zinc-500 hover:text-zinc-700 transition-colors"
                      >
                        View All Runs ({historyData?.executions?.length || 0})
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Jump to Step Section - Only show when workflow is running and not viewing history */}
        {isWorkflowRunning && !selectedRunId && !showHistoryList && (
          <div className="flex items-center gap-2 pt-3 border-t shrink-0">
            <div className="flex-1 flex items-center gap-2">
              <SkipForward className="h-4 w-4 text-zinc-500 shrink-0" />
              <Select
                value={selectedStepIndex}
                onValueChange={setSelectedStepIndex}
              >
                <SelectTrigger className="h-8 text-xs flex-1">
                  <SelectValue placeholder="Jump to step..." />
                </SelectTrigger>
                <SelectContent>
                  {availableSteps.map((step) => (
                    <SelectItem
                      key={step.id}
                      value={String(step.index)}
                      className="text-xs"
                    >
                      {step.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                type="button"
                onClick={handleJumpToStep}
                disabled={!selectedStepIndex || isJumping}
                className="px-3 py-1.5 text-xs font-semibold rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              >
                {isJumping ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  'Jump'
                )}
              </button>
            </div>
            {/* Cancel button for running workflows */}
            <button
              type="button"
              onClick={handleCancel}
              disabled={isCancelling}
              className="px-3 py-1.5 text-xs font-semibold rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed shrink-0 flex items-center gap-1"
            >
              {isCancelling ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <X className="h-3.5 w-3.5" />
              )}
              Cancel
            </button>
          </div>
        )}

        {/* Cancel Button Section - Show when workflow is in error state (full width) */}
        {isWorkflowError && !selectedRunId && (
          <div className="flex items-center justify-center gap-2 pt-3 border-t shrink-0">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isCancelling}
              className="px-4 py-2 text-sm font-semibold rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isCancelling ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <X className="h-4 w-4" />
              )}
              Cancel Workflow
            </button>
          </div>
        )}

        {/* Restart Section - Show for terminated/cancelled/failed workflows */}
        {canRestart && hasWorkflowInput && !showHistoryList && (
          <div className="flex items-center gap-2 pt-3 border-t shrink-0">
            <div className="flex-1 flex items-center gap-2">
              <Play className="h-4 w-4 text-emerald-600 shrink-0" />
              <Select
                value={restartStepIndex}
                onValueChange={setRestartStepIndex}
              >
                <SelectTrigger className="h-8 text-xs flex-1">
                  <SelectValue placeholder="Start from step..." />
                </SelectTrigger>
                <SelectContent>
                  {restartStepOptions.map((step) => (
                    <SelectItem
                      key={step.id}
                      value={String(step.index)}
                      className="text-xs"
                    >
                      {step.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                type="button"
                onClick={handleRestart}
                disabled={isRestarting}
                className="px-3 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed shrink-0 flex items-center gap-1"
              >
                {isRestarting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Play className="h-3.5 w-3.5" />
                )}
                Restart
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center pt-2 border-t shrink-0">
          <div className="text-xs text-zinc-500">
            {showHistoryList && !selectedRunId
              ? `${historyData?.executions?.length || 0} run${(historyData?.executions?.length || 0) !== 1 ? 's' : ''} available`
              : selectedRunId
                ? 'Viewing historical run'
                : isWorkflowRunning
                  ? `Auto-refreshing every ${refreshInterval / 1000}s`
                  : 'Workflow not running'}
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-3 py-2 text-xs font-semibold rounded-md border border-zinc-200 text-zinc-600 hover:bg-zinc-50"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AgentFlowHistory;
